/**
 * Live go-to chip metrics (spec 13): straight rhumb-line distance/bearing to
 * the pinned point per GPS fix, plus an ETA from live SOG. Deliberately not
 * follow-route (05) — no XTE, no auto-advance, one leg. Same vanilla-store
 * shape as followMetrics.ts.
 */
import { createStore } from 'zustand/vanilla';
import { rhumbDistanceNm, rhumbBearingDeg, type LatLon } from '@boatnav/core';
import { posStore, type Fix } from '../nav/posStore.js';
import { useGotoStore } from './gotoStore.js';

export interface GotoMetrics {
  distNm: number;
  brgDeg: number;
  etaMs: number | null;
}

/** ETA requires the boat to actually be making way — matches the chip's "—"
 *  fallback below 1 kn (spec 13; follow-route uses a lower 0.5 kn floor for
 *  its own different purpose, auto-advance hysteresis). */
const ETA_MIN_SOG_KN = 1;

/** Pure so it's unit-testable against a fixture fix without touching the
 *  DOM/stores (spec 13 acceptance: "chip math against core functions"). */
export function computeGotoMetrics(fix: Fix, target: LatLon, now: number = Date.now()): GotoMetrics {
  const here = { lat: fix.lat, lon: fix.lon };
  const distNm = rhumbDistanceNm(here, target);
  const brgDeg = rhumbBearingDeg(here, target);
  const etaMs = fix.sogKn != null && fix.sogKn >= ETA_MIN_SOG_KN ? now + (distNm / fix.sogKn) * 3_600_000 : null;
  return { distNm, brgDeg, etaMs };
}

export const gotoMetricsStore = createStore<{ metrics: GotoMetrics | null }>(() => ({ metrics: null }));

let unsubPos: (() => void) | null = null;
let unsubGoto: (() => void) | null = null;

function recompute(fix: Fix | null): void {
  const target = useGotoStore.getState().target;
  if (!target || !fix) {
    gotoMetricsStore.setState({ metrics: null });
    return;
  }
  gotoMetricsStore.setState({ metrics: computeGotoMetrics(fix, target.point) });
}

export function startGotoMetrics(): void {
  if (unsubPos) return;
  recompute(posStore.getState().fix);
  unsubPos = posStore.subscribe((s, prev) => {
    if (s.fix !== prev.fix) recompute(s.fix);
  });
  unsubGoto = useGotoStore.subscribe(() => recompute(posStore.getState().fix));
}

export function stopGotoMetrics(): void {
  unsubPos?.();
  unsubPos = null;
  unsubGoto?.();
  unsubGoto = null;
  gotoMetricsStore.setState({ metrics: null });
}
